/**
 * Paginated list of information-meeting slots with join / cancel actions.
 */

import * as React from 'react';
import { Calendar, CheckCircle2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DataTablePagination } from '@/components/common/data-table/DataTablePagination';
import { Spinner } from '@/components/ui/spinner';
import type { AvailableMeeting } from '@/types/remote/session.types';
import {
  addSessionParticipant,
  deleteSessionParticipant,
  getSession,
  getSessions,
} from '@/services/remote/sessions.service';
import {
  clearInformationSessionReservation as clearRemoteInformationSession,
  reserveInformationSession as reserveRemoteInformationSession,
} from '@/services/remote/tutors.service';
import {
  clearInformationSessionReservation as clearInPersonInformationSession,
  reserveInformationSession as reserveInPersonInformationSession,
} from '@/services/inperson/tutors.service';
import {
  clearWelcomeMeetingReservation,
  getBookableWelcomeMeetings,
  reserveWelcomeMeeting,
} from '@/services/remote/students.service';
import { toLocalDateString } from '@/utils/format-date-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  buildAvailableMeetingsQueryParams,
  findSelectedMeeting,
  isMeetingInThePast,
  resolveDisplayedMeeting,
  resolveEditCancelParticipantId,
  shouldClearOrphanMeetingSelection,
  toAvailableMeeting,
} from '@/utils/meeting-selector-utils';
import { toast } from '@/components/ui/toast';

/** Props for {@link MeetingSelector}. */
interface MeetingSelectorProps {
  type: 'information_meeting' | 'information_meeting_in_person' | 'welcome_meeting';
  userId: string;
  companyId: string;
  value: string | null;
  onChange: (sessionId: string | null) => void;
  pageSize?: number;
  /** Create is local-only; edit joins/cancels via the API. */
  mode?: 'create' | 'edit';
}

/** Persist `sessionId: null` on the onboarding record after an edit-mode cancel. */
async function persistCancelledMeetingBooking(
  type: MeetingSelectorProps['type'],
  userId: string,
  companyId: string,
): Promise<void> {
  if (type === 'information_meeting') {
    await clearRemoteInformationSession(userId, companyId);
    return;
  }
  if (type === 'information_meeting_in_person') {
    await clearInPersonInformationSession(userId, companyId);
    return;
  }
  await clearWelcomeMeetingReservation(userId, companyId);
}

/** Persist the new `sessionId` on the onboarding record after an edit-mode join. */
async function persistReservedMeetingBooking(
  type: MeetingSelectorProps['type'],
  userId: string,
  companyId: string,
  sessionId: string,
): Promise<void> {
  if (type === 'information_meeting') {
    await reserveRemoteInformationSession(userId, companyId, sessionId);
    return;
  }
  if (type === 'information_meeting_in_person') {
    await reserveInPersonInformationSession(userId, companyId, sessionId);
    return;
  }
  await reserveWelcomeMeeting(userId, companyId, sessionId);
}

/** Selector for pairing a meeting with a session. */
export const MeetingSelector: React.FC<MeetingSelectorProps> = ({
  type,
  userId,
  companyId,
  value,
  onChange,
  pageSize = 5,
  mode = 'edit',
}) => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [participantId, setParticipantId] = React.useState<string | null>(null);

  async function loadAvailableMeetings(): Promise<AvailableMeeting[]> {
    if (type === 'welcome_meeting') {
      const result = await getBookableWelcomeMeetings(companyId, toLocalDateString(new Date()));
      return result.data.map((session) =>
        toAvailableMeeting(
          {
            id: session.id,
            sessionDate: session.sessionDate,
            startTime: session.startTime,
            endTime: session.endTime,
            type: session.type,
          },
          userId,
        ),
      );
    }
    const result = await getSessions(buildAvailableMeetingsQueryParams(type));
    return result.data.map((session) =>
      toAvailableMeeting(
        {
          id: session.id,
          sessionDate: session.sessionDate,
          startTime: session.startTime,
          endTime: session.endTime,
          availableSlots: (session as { availableSlots?: number }).availableSlots,
          type: session.type,
          participants: session.participants,
        },
        userId,
      ),
    );
  }

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['available-meetings', type, companyId],
    queryFn: loadAvailableMeetings,
    enabled: !!companyId,
  });
  const meetings = data ?? [];
  const listedSelected = findSelectedMeeting(value, meetings);
  const {
    data: bookedSession,
    isFetched: bookedFetched,
    isError: bookedError,
    isLoading: bookedLoading,
  } = useQuery({
    queryKey: ['session', value],
    queryFn: () => getSession(value as string),
    enabled: Boolean(value) && !listedSelected,
  });
  const bookedMeeting = bookedSession
    ? toAvailableMeeting(
        {
          id: bookedSession.id,
          sessionDate: bookedSession.sessionDate,
          startTime: bookedSession.startTime,
          endTime: bookedSession.endTime,
          availableSlots: (bookedSession as { availableSlots?: number }).availableSlots,
          type: bookedSession.type,
          participants: bookedSession.participants,
        },
        userId,
      )
    : null;
  const heldSelectedRef = React.useRef<AvailableMeeting | null>(null);
  const selectedMeeting = resolveDisplayedMeeting(
    value,
    meetings,
    bookedMeeting,
    heldSelectedRef.current,
  );
  if (selectedMeeting) {
    heldSelectedRef.current = selectedMeeting;
  } else if (!value) {
    heldSelectedRef.current = null;
  }
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const bookedLookupSettled = !value || Boolean(listedSelected) || bookedFetched || bookedError;

  React.useEffect(() => {
    if (!isSuccess) return;
    if (
      !shouldClearOrphanMeetingSelection({
        value,
        meetings,
        bookedMeetingId: bookedMeeting?.id ?? null,
        bookedLookupSettled,
      })
    ) {
      return;
    }
    onChangeRef.current(null);
  }, [isSuccess, value, meetings, bookedMeeting, bookedLookupSettled]);

  const joinMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      if (mode === 'create') {
        return { sessionId, participantId: 'pending' };
      }

      const role =
        type === 'information_meeting' || type === 'information_meeting_in_person'
          ? 'tutor'
          : 'student';

      const data = await addSessionParticipant(sessionId, { userId, role });
      if (userId && companyId) {
        await persistReservedMeetingBooking(type, userId, companyId, sessionId);
      }
      return { sessionId, participantId: data.id };
    },
    onSuccess: ({ sessionId, participantId: pId }) => {
      setParticipantId(pId);
      onChange(sessionId);
      if (mode === 'edit') {
        void queryClient.invalidateQueries({ queryKey: ['available-meetings', type, companyId] });
        void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      if (mode === 'edit') {
        const session = await getSession(sessionId);
        const participantToDelete = resolveEditCancelParticipantId({
          userId,
          knownParticipantId: participantId,
          enrolledParticipantId: selectedMeeting?.enrolledParticipantId,
          participants: session.participants,
        });
        if (participantToDelete) {
          await deleteSessionParticipant(sessionId, participantToDelete);
        }
        if (userId && companyId) {
          await persistCancelledMeetingBooking(type, userId, companyId);
        }
      }
    },
    onSuccess: () => {
      setParticipantId(null);
      onChange(null);
      if (mode === 'edit') {
        void queryClient.invalidateQueries({ queryKey: ['available-meetings', type, companyId] });
        void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const totalItems = selectedMeeting ? 1 : meetings.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentMeetings = selectedMeeting
    ? [selectedMeeting]
    : meetings.slice(startIndex, endIndex);
  const isResolvingSelection = Boolean(value) && !selectedMeeting && bookedLoading;
  const showSpinner = isLoading || isResolvingSelection;
  const isEmpty = !showSpinner && !meetings.length && !selectedMeeting;
  const showList = !showSpinner && !isEmpty;
  const showPagination = !selectedMeeting && totalPages > 1;

  function handleCancelSelected(): void {
    if (!value) return;
    cancelMutation.mutate(value);
  }

  function renderSelectedActions(isPast: boolean): React.ReactNode {
    return (
      <>
        <LabelBadge label="Inscrit" variant="inscrit" icon={CheckCircle2} />
        {!isPast && (
          <Button
            preset="meeting-cancel"
            onClick={handleCancelSelected}
            disabled={cancelMutation.isPending}
            loading={cancelMutation.isPending}
          />
        )}
      </>
    );
  }

  function renderRowActions(meeting: AvailableMeeting): React.ReactNode {
    const isSelected = selectedMeeting?.id === meeting.id;
    if (isSelected) return renderSelectedActions(isMeetingInThePast(meeting));
    if (selectedMeeting) return null;

    function handleJoin(): void {
      joinMutation.mutate(meeting.id);
    }

    return (
      <Button
        preset="meeting-join"
        onClick={handleJoin}
        disabled={joinMutation.isPending}
        loading={joinMutation.isPending}
      />
    );
  }

  function renderMeetingRow(meeting: AvailableMeeting): React.ReactNode {
    return (
      <div
        key={meeting.id}
        className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[color-mix(in_srgb,var(--color-1-nuance-3)_40%,transparent)] last:border-b-0"
      >
        <div className="flex-1">
          <p className="font-medium">
            {new Date(meeting.sessionDate).toLocaleDateString('fr-FR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {meeting.startTime} - {meeting.endTime}
            {meeting.availableSlots !== undefined &&
              ` • ${meeting.availableSlots} places disponibles`}
          </p>
        </div>
        <div className="flex items-center gap-3">{renderRowActions(meeting)}</div>
      </div>
    );
  }

  return (
    <div className="table-container">
      {showSpinner && (
        <div className="table-empty">
          <Spinner size="lg" label="Chargement des réunions..." />
        </div>
      )}

      {isEmpty && (
        <div className="table-empty">
          <Calendar className="table-empty-icon" />
          <p className="table-empty-text">Aucune réunion disponible pour le moment.</p>
        </div>
      )}

      {showList && (
        <>
          <div>{currentMeetings.map(renderMeetingRow)}</div>
          {showPagination && (
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              startIndex={startIndex}
              endIndex={endIndex}
              itemLabel="réunions"
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};
